import * as Sentry from '@sentry/browser';
import type { FlashMessageType } from '../utils/flashMessage';
import {
  NetworkError,
  ResponseError,
  TimeoutError,
  UnexpectedHtmlError,
} from './errors';

export const GENERIC_MESSAGE =
  '問題が発生しました。時間を空けて再度お試しください。';
export const TIMEOUT_MESSAGE =
  'タイムアウトしました。通信環境をご確認の上、時間を空けて再度お試しください。';
export const SESSION_EXPIRED_MESSAGE =
  'ログインセッションが切れました。再度ログインしてください。';

// notify は既存の実装をケアするために設定している。
// 今後廃止予定なので新規の実装で使ってはいけない。
export function handleHttpError(
  error: unknown,
  notify?: (message: string, type: FlashMessageType) => void,
): void {
  // 呼び出し側による中断はキャンセルであり失敗ではないので、通知も Sentry 送信もしない
  if (isAbortError(error)) return;

  notify?.(buildErrorMessage(error), 'danger');

  // ネットワーク断とセッション切れ・CSRF 失敗の HTML は利用者の状態が原因なので Sentry に送らない（config/sentry.ts の ignoreErrors と同じ意図）。
  // Api::ApiController 系の CSRF 失敗は 422 の HTML で返り ClientError になるため、ここでは除外できない
  if (
    !(error instanceof NetworkError || error instanceof UnexpectedHtmlError)
  ) {
    Sentry.captureException(error);
  }
}

export function buildErrorMessage(error: unknown): string {
  if (error instanceof TimeoutError) return TIMEOUT_MESSAGE;
  if (error instanceof UnexpectedHtmlError) return SESSION_EXPIRED_MESSAGE;
  if (!(error instanceof ResponseError)) return GENERIC_MESSAGE;
  // 408 は ALB や Nginx のタイムアウト
  if (error.status === 408) return TIMEOUT_MESSAGE;

  const { body } = error;
  if (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof body.message === 'string'
  ) {
    return body.message;
  }
  return GENERIC_MESSAGE;
}

// AbortError は DOMException とは限らず、別 realm の DOMException は instanceof Error も false になる（Jest など）ため、name で判定する
function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  );
}
